import React, { useEffect, useState } from "react";
import style from "./AnetModal.module.scss";
import Modal from "../../../../components/modal";
import Button from "../../../../components/new-button";
import toast from "react-hot-toast";
import moment from "moment";
import { getAnetConfig, approveAnetApplication } from "../../../../../api";
import { extractErrorMessage } from "../../../../utils/extract-error-message";
import TextInput from "../../../../components/form/text-input";
import FancyCheckBox from "../../../../components/form/fancy-checkbox";
import FancyRadioInput from "../../../../components/form/fancy-radio-input";
import HeadingWithLine from "../../../../components/HeadingWithLine/HeadingWithLine";
import StatusDot from "../../../../components/StatusDot/StatusDot";
import { getAnetStatusColor } from "../../../../utils/anet-status-colors";

// Fallback only, used if getAnetConfig() fails. Keep in sync with config/anet.php.
const DEFAULT_PROCESSORS = [
  { value: "Elavon", label: "Elavon", id: 1 },
  { value: "TSYS", label: "TSYS", id: 2 },
  { value: "NABEPX", label: "NABEPX", id: 3 },
  { value: "FirstDataNashville", label: "First Data Nashville", id: 4 },
  { value: "FirstDataOmaha", label: "First Data Omaha", id: 5 },
  { value: "GlobalPayment", label: "Global Payment", id: 6 },
];

const DEFAULT_PAYMENT_TYPES = ["Visa", "Mastercard", "Discover", "AmericanExpress", "JCB", "Diners"];

const DEFAULT_PROCESSOR_REQUIREMENTS = {
  Elavon: ["bankNumber", "terminalNumber"],
  TSYS: ["merchantNumber", "bankNumber", "terminalNumber", "bin", "chain", "storeNumber", "mcc"],
  NABEPX: ["merchantNumber", "customerNumber", "terminalNumber", "dbaLocation"],
  FirstDataNashville: ["merchantNumber", "terminalNumber"],
  FirstDataOmaha: ["merchantNumber"],
  GlobalPayment: ["bankNumber", "terminalNumber"],
};

const MCC_PATTERN = /^\d{4}$/;
const NUMERIC_FIELDS = ["merchantNumber", "bankNumber", "terminalNumber", "bin", "chain", "storeNumber"];
// Filled from the Business Information MCC so it isn't asked for twice.
const DERIVED_FIELDS = ["mcc"];
const digitsOnly = (value) => value.replace(/\D/g, "");

function AnetApprovalModal({ isOpen, onClose, appData, anetData, onSuccess }) {
  const [processor, setProcessor] = useState("");
  const [mcc, setMcc] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [processorInfo, setProcessorInfo] = useState({});
  const [paymentTypes, setPaymentTypes] = useState([
    "Visa",
    "Mastercard",
    "Discover",
  ]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [processorOptions, setProcessorOptions] = useState(DEFAULT_PROCESSORS);
  const [paymentTypeOptions, setPaymentTypeOptions] = useState(DEFAULT_PAYMENT_TYPES);
  const [processorRequirements, setProcessorRequirements] = useState(DEFAULT_PROCESSOR_REQUIREMENTS);

  useEffect(() => {
    if (isOpen) {
      fetchConfig();
    }
  }, [isOpen]);

  const fetchConfig = async () => {
    try {
      const response = await getAnetConfig();
      if (response.status) {
        const { processors, processor_requirements, payment_types } = response.data;
        if (processors?.length) setProcessorOptions(processors);
        if (processor_requirements) setProcessorRequirements(processor_requirements);
        if (payment_types?.length) setPaymentTypeOptions(payment_types);
      }
    } catch (error) {
      console.error("Failed to fetch ANET config, using defaults:", error);
    }
  };

  useEffect(() => {
    if (isOpen && appData) {
      setBusinessName(appData.field_dba || appData.field_legalname || "");
      setMcc(appData.mcc_info?.mcc ? String(appData.mcc_info.mcc) : "");
      setProcessor("");
      setProcessorInfo({});
      setErrors({});
    }
  }, [isOpen, appData]);

  const getInputFields = (proc) =>
    (processorRequirements[proc] || []).filter((field) => !DERIVED_FIELDS.includes(field));

  useEffect(() => {
    if (processor) {
      const initialInfo = {};
      getInputFields(processor).forEach((field) => {
        initialInfo[field] = "";
      });
      setProcessorInfo(initialInfo);
    }
    // Deliberately only re-seeds on a processor change, not on processorRequirements
    // updating in the background (e.g. the config fetch resolving after the user has
    // already started typing) - otherwise it silently wipes what they just typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processor]);

  const validateForm = () => {
    const newErrors = {};

    if (!processor) newErrors.processor = "Processor is required";
    if (!mcc) {
      newErrors.mcc = "MCC is required";
    } else if (!MCC_PATTERN.test(mcc)) {
      newErrors.mcc = "MCC must be exactly 4 digits";
    }
    if (!businessName) newErrors.businessName = "Business name is required";

    if (processor) {
      getInputFields(processor).forEach((field) => {
        if (!processorInfo[field]) {
          newErrors[`processorInfo.${field}`] = `${getFieldLabel(field)} is required`;
        } else if (NUMERIC_FIELDS.includes(field) && !/^\d+$/.test(processorInfo[field])) {
          newErrors[`processorInfo.${field}`] = `${getFieldLabel(field)} must contain digits only`;
        }
      });
    }

    if (paymentTypes.length === 0) {
      newErrors.paymentTypes = "Select at least one payment type";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleApprove = async () => {
    if (!validateForm()) {
      toast.error("Please fill all required fields");
      return;
    }

    setLoading(true);
    const toastId = toast.loading("Approving application to ANET...");

    try {
      const response = await approveAnetApplication(appData.id, {
        processor,
        mcc,
        businessName,
        processorInfo: {
          ...processorInfo,
          ...((processorRequirements[processor] || []).includes("mcc") ? { mcc } : {}),
          paymentTypes,
        },
      });

      if (response.status) {
        toast.success("Application approved successfully!", { id: toastId });
        onSuccess && onSuccess();
        onClose();
      } else {
        toast.error(response.message || "Approval failed", { id: toastId });
      }
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to approve application"), { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentTypeToggle = (type) => {
    if (paymentTypes.includes(type)) {
      setPaymentTypes(paymentTypes.filter((t) => t !== type));
    } else {
      setPaymentTypes([...paymentTypes, type]);
    }
  };

  const handleProcessorInfoChange = (field, value) => {
    setProcessorInfo({
      ...processorInfo,
      [field]: NUMERIC_FIELDS.includes(field) ? digitsOnly(value) : value,
    });
    const errorKey = `processorInfo.${field}`;
    if (errors[errorKey]) {
      setErrors({ ...errors, [errorKey]: null });
    }
  };

  const clearFieldOnChange = (setter, field) => (e) => {
    setter(e.target.value);
    setErrors({ ...errors, [field]: null });
  };

  const getFieldLabel = (field) => {
    return field
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase());
  };

  return (
    <Modal
      header={`Approve Application`}
      isOpen={isOpen}
      onClose={onClose}
      size={"lg"}
      customClass={style.modal_parent}
    >
      <div className={style.modal_content}>
        <div className={style.detail_box}>
          <div className={style.detail_grid}>
            <div className={style.detail_item}>
              <span>ANET ID</span>
              <span>{anetData?.anet_application_id}</span>
            </div>
            <div className={style.detail_item}>
              <span>Status</span>
              <span className={style.status_value}>
                <StatusDot color={getAnetStatusColor(anetData?.anet_status)} />
                {anetData?.anet_status}
              </span>
            </div>
            <div className={style.detail_item}>
              <span>Gateway ID</span>
              <span>{anetData?.anet_gateway_id || "N/A"}</span>
            </div>
            <div className={style.detail_item}>
              <span>Submission Date</span>
              <span>
                {anetData?.anet_app_submission_date
                  ? moment(anetData.anet_app_submission_date).format("MMM DD, YYYY")
                  : "N/A"}
              </span>
            </div>
          </div>
        </div>

        {anetData?.anet_status !== "Underwriting" && (
          <p className={style.error_text_loose} style={{ color: "inherit", opacity: 0.7 }}>
            This application is currently {anetData?.anet_status}. Approving will
            also move it through the remaining required stages automatically.
          </p>
        )}

        <div className={style.section}>
          <HeadingWithLine text="Select Processor" margin={false} />
          <div className={style.field_with_error}>
            <div className={style.processor_options_row}>
              {processorOptions.map((proc) => (
                <FancyRadioInput
                  key={proc.value}
                  id={`anet-processor-${proc.value}`}
                  name="processor"
                  label={proc.label}
                  value={proc.value}
                  checked={processor === proc.value}
                  onChange={(e) => {
                    setProcessor(e.target.value);
                    setErrors({ ...errors, processor: null });
                  }}
                />
              ))}
            </div>
            {errors.processor && <p className={style.error_text_loose}>{errors.processor}</p>}
          </div>
        </div>

        {processor && getInputFields(processor).length > 0 && (
          <div className={style.section}>
            <HeadingWithLine text={`Processor-Specific Information (${processorOptions.find((p) => p.value === processor)?.label || processor})`} margin={false} />
            <div className={style.processor_grid}>
              {getInputFields(processor).map((field) => (
                <div key={field}>
                  <TextInput
                    id={`anet-processor-${field}`}
                    name={`processorInfo.${field}`}
                    label={`${getFieldLabel(field)} *`}
                    value={processorInfo[field] || ""}
                    onChange={(e) =>
                      handleProcessorInfoChange(field, e.target.value)
                    }
                    placeholder={getFieldLabel(field)}
                    touched
                    errored={!!errors[`processorInfo.${field}`]}
                    error={errors[`processorInfo.${field}`]}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={style.section}>
          <HeadingWithLine text="Business Information" margin={false} />
          <div className={style.field_grid}>
            <div className={style.field_group}>
              <TextInput
                id="anet-mcc"
                name="mcc"
                label="MCC Code *"
                value={mcc}
                onChange={(e) => {
                  setMcc(digitsOnly(e.target.value));
                  setErrors({ ...errors, mcc: null });
                }}
                placeholder="e.g., 5812"
                maxLength={4}
                touched
                errored={!!errors.mcc}
                error={errors.mcc}
              />
            </div>

            <div className={style.field_group}>
              <TextInput
                id="anet-business-name"
                name="businessName"
                label="Business Name *"
                value={businessName}
                onChange={clearFieldOnChange(setBusinessName, "businessName")}
                placeholder="Business Name"
                touched
                errored={!!errors.businessName}
                error={errors.businessName}
              />
            </div>
          </div>
        </div>

        <div className={style.section}>
          <HeadingWithLine text="Payment Types" margin={false} />
          <div className={style.field_with_error}>
            <div className={style.payment_types_row}>
              {paymentTypeOptions.map((type) => (
                <FancyCheckBox
                  key={type}
                  id={`anet-payment-type-${type}`}
                  name={type}
                  label={type}
                  checked={paymentTypes.includes(type)}
                  onChange={() => handlePaymentTypeToggle(type)}
                />
              ))}
            </div>
            {errors.paymentTypes && <p className={style.error_text_loose}>{errors.paymentTypes}</p>}
          </div>
        </div>

        <footer className={style.footer}>
          <Button type="button" variant="ghost" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="button" onClick={handleApprove} disabled={loading}>
            {loading ? "Approving..." : "Approve"}
          </Button>
        </footer>
      </div>
    </Modal>
  );
}

export default AnetApprovalModal;
